import { homepageData } from "@/mocks/homeData";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";

export default function AboutSection() {
  const { about } = homepageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.12, sectionIndex: 0 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-50"
      style={{ minHeight: "100svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start max-w-5xl mx-auto">
          {/* Left: decorative marker */}
          <div className="lg:w-[200px] shrink-0 flex lg:flex-col items-center lg:items-start gap-4 lg:pt-2">
            <div
              className={`w-px h-20 lg:h-32 bg-primary-300 transition-all duration-700 ease-out ${
                isVisible ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"
              }`}
              style={{ transformOrigin: "top", transitionDelay: "0ms" }}
            />
            <div
              className={`transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
              }`}
              style={{ transitionDelay: "100ms" }}
            >
              <span className="font-heading text-5xl md:text-6xl font-bold text-primary-200 leading-none block">
                01
              </span>
            </div>
          </div>

          {/* Right: content */}
          <div className="flex-1 min-w-0">
            {/* Title + subtitle */}
            <div
              className={`transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 tracking-wide leading-tight">
                {about.title}
              </h2>
              <p className="mt-3 text-base md:text-lg text-foreground-500 leading-relaxed">
                {about.subtitle}
              </p>
            </div>

            {/* Formal name + English name */}
            <div
              className={`mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "130ms" }}
            >
              <span className="text-foreground-500">
                正式名稱：
                <strong className="text-foreground-800 font-medium">{about.formalName}</strong>
              </span>
              <span className="hidden sm:inline text-foreground-300 select-none">|</span>
              <span className="text-foreground-500">
                英文名稱：
                <strong className="text-foreground-800 font-medium">{about.englishName}</strong>
              </span>
            </div>

            {/* Intro paragraph */}
            <div
              className={`mt-6 transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "180ms" }}
            >
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {about.intro}
              </p>
            </div>

            {/* Two series cards */}
            <div
              className={`mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "250ms" }}
            >
              {about.series.map((series) => (
                <div
                  key={series.id}
                  className="group bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 transition-all duration-300 hover:border-primary-200 hover:-translate-y-1"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-4 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors duration-300">
                    <i className={`${series.icon} text-lg`} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    {series.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {series.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Brand relationship + catalog info box */}
            <div
              className={`mt-8 bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "340ms" }}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-1 h-4 rounded-full bg-accent-500" />
                <h4 className="text-xs tracking-[0.2em] uppercase text-foreground-400">
                  品牌經銷關係
                </h4>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                {about.relationText}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {about.catalogText}
              </p>
            </div>

            {/* CTA button */}
            <div
              className={`mt-6 transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "420ms" }}
            >
              <a
                href={about.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/20"
              >
                {about.ctaLabel}
                <i className="ri-external-link-line text-xs transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}